import { confirmInvoicePayment } from "./payment-contract";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreatePaymentIntent } from "@/data/invoice";
import type { PaymentIntent, StripeError } from "@stripe/stripe-js";

/**
 * Paying an invoice, deferred-intent shaped.
 *
 * The same three steps as the checkout, against the invoice's own mutation: `elements.submit()`,
 * then `POST /invoices/:id/pay` with the email, then `confirmPayment`. The mutation is the
 * existing one and is unchanged; what changed is WHEN it runs. It used to fire on mount with
 * whatever email the customer record happened to carry, which on a customer with no email meant
 * the page posted an empty string and died on a raw Joi string, `"email" is not allowed to be
 * empty`, with a Try again that reloaded straight back into it. Now it runs on Pay, with an
 * address the page has actually asked for.
 */

export type InvoiceOutcome = {
  kind: "succeeded" | "processing";
  email: string;
};

export interface UseInvoicePaymentOptions {
  invoiceId: string;
  onDone: (outcome: InvoiceOutcome) => void;
}

export function useInvoicePayment({
  invoiceId,
  onDone,
}: UseInvoicePaymentOptions) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const createIntent = useCreatePaymentIntent();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pay = useCallback(
    async (email: string): Promise<boolean> => {
      if (!stripe || !elements) return false;
      setError(null);
      setPaying(true);

      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setPaying(false);
          return false;
        }

        const intent = await createIntent.mutateAsync({ invoiceId, email });

        const { error: confirmError, paymentIntent } =
          await confirmInvoicePayment({
            stripe,
            elements,
            clientSecret: intent.clientSecret,
            email,
            returnUrl: window.location.href,
          });

        if (confirmError) {
          setError(messageFor(confirmError, t));
          setPaying(false);
          return false;
        }

        const outcome = outcomeFor(paymentIntent, email);
        if (outcome) {
          onDone(outcome);
          return true;
        }

        setError(t("gymkeePay.checkout.bankRefused"));
        setPaying(false);
        return false;
      } catch {
        setError(t("gymkeePay.checkout.intentFailed"));
        setPaying(false);
        return false;
      }
    },
    [stripe, elements, createIntent, invoiceId, onDone, t],
  );

  return { pay, paying, error, ready: !!stripe && !!elements };
}

function outcomeFor(
  intent: PaymentIntent | undefined,
  email: string,
): InvoiceOutcome | null {
  if (!intent) return null;
  if (intent.status === "succeeded" || intent.status === "processing") {
    return { kind: intent.status, email };
  }
  return null;
}

function messageFor(error: StripeError, t: (key: string) => string): string {
  if (error.type === "card_error" || error.type === "validation_error") {
    return t("gymkeePay.checkout.bankRefused");
  }
  return t("gymkeePay.checkout.intentFailed");
}
